import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import ReactApexChart from 'react-apexcharts'
import toast from 'react-hot-toast'
import {
  FileBarChart, Printer, Download, Package, Users, TrendingUp, Boxes, Megaphone,
} from 'lucide-react'
import { reportService, productService, customerService, categoryService } from '../services/api'
import { Spinner } from '../components/ui'

const money = (v) => 'R' + Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const numf = (v) => Number(v || 0).toLocaleString('en-ZA')
const arr = (d) => (Array.isArray(d) ? d : (d?.items ?? d?.customers ?? d?.products ?? []))

// ── CSV + print helpers ───────────────────────────────────────────────────
function toCSV(columns, rows) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const head = columns.map(c => esc(c.header)).join(',')
  const body = rows.map(row => columns.map(c => esc(c.csv ? c.csv(row) : row[c.key])).join(',')).join('\n')
  return head + '\n' + body
}
function downloadCSV(filename, text) {
  const blob = new Blob(['\ufeff' + text], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  URL.revokeObjectURL(url)
}
function printReport(title, subtitle, columns, rows) {
  const esc = (s) => String(s ?? '').replace(/[&<>]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]))
  const th = columns.map(c => `<th style="text-align:${c.align || 'left'}">${esc(c.header)}</th>`).join('')
  const trs = rows.map(row => '<tr>' + columns.map(c =>
    `<td style="text-align:${c.align || 'left'}">${esc(c.print ? c.print(row) : row[c.key])}</td>`).join('') + '</tr>').join('')
  const w = window.open('', '_blank')
  if (!w) { toast.error('Allow pop-ups to print'); return }
  w.document.write(`<!doctype html><html><head><title>${esc(title)}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:28px;}
      h1{color:#31348B;font-size:20px;margin:0 0 2px;}
      .sub{color:#666;font-size:12px;margin:0 0 18px;}
      table{width:100%;border-collapse:collapse;font-size:12px;}
      th{background:#31348B;color:#fff;padding:7px 9px;text-align:left;}
      td{padding:6px 9px;border-bottom:1px solid #e5e7eb;}
      tr:nth-child(even) td{background:#f7f7fb;}
      .foot{margin-top:16px;color:#999;font-size:10px;}
    </style></head><body>
    <h1>Tony's Liquor Distributor</h1>
    <p class="sub">${esc(title)}${subtitle ? ' — ' + esc(subtitle) : ''} · generated ${new Date().toLocaleString('en-ZA')}</p>
    <table><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>
    <p class="foot">${rows.length} rows</p>
    </body></html>`)
  w.document.close(); w.focus(); setTimeout(() => w.print(), 250)
}

// ── Report definitions ──────────────────────────────────────────────────────
const REPORTS = [
  { key: 'sales',       label: 'Sales Summary',      icon: TrendingUp, dated: true },
  { key: 'cust_orders', label: 'Customer Orders',    icon: Users, dated: true },
  { key: 'promo_perf',  label: 'Promotion Performance', icon: Megaphone },
  { key: 'top',         label: 'Top Products',       icon: TrendingUp },
  { key: 'categories',  label: 'Category Breakdown', icon: Boxes },
  { key: 'catalogue',   label: 'Product Catalogue',  icon: Package },
  { key: 'customers',   label: 'Customer List',      icon: Users },
]

export default function Reports() {
  const [active, setActive] = useState('sales')
  const today = new Date().toISOString().slice(0, 10)
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10)
  const [from, setFrom] = useState(monthAgo)
  const [to, setTo] = useState(today)

  const report = REPORTS.find(r => r.key === active)

  const salesQ = useQuery({ queryKey: ['rep', 'sales', from, to], queryFn: () => reportService.getSalesSummary(from, to), enabled: active === 'sales' })
  const custOrdersQ = useQuery({ queryKey: ['rep', 'custorders', from, to], queryFn: () => reportService.getCustomerOrders(from, to), enabled: active === 'cust_orders' })
  const promoPerfQ = useQuery({ queryKey: ['rep', 'promoperf'], queryFn: () => reportService.getPromotionPerformance(), enabled: active === 'promo_perf' })
  const topQ = useQuery({ queryKey: ['rep', 'top'], queryFn: () => reportService.getTopProducts(50), enabled: active === 'top' })
  const catQ = useQuery({ queryKey: ['rep', 'cat'], queryFn: () => reportService.getCategoryBreakdown(), enabled: active === 'categories' })
  const catalogueQ = useQuery({ queryKey: ['rep', 'catalogue'], queryFn: () => productService.getAdminList({ page: 1, limit: 5000 }), enabled: active === 'catalogue' })
  const custQ = useQuery({ queryKey: ['rep', 'cust'], queryFn: () => customerService.getAll({ limit: 5000 }), enabled: active === 'customers' })
  const { data: cats } = useQuery({ queryKey: ['rep', 'cat-map'], queryFn: () => categoryService.getAll(), staleTime: 5 * 60 * 1000 })
  const catName = useMemo(() => {
    const m = {}; arr(cats).forEach(c => { m[c.id] = c.name }); return m
  }, [cats])

  const { columns, rows, loading, summary, chart } = useMemo(() => {
    if (active === 'sales') {
      const d = salesQ.data || {}
      return {
        loading: salesQ.isLoading,
        summary: [
          { label: 'Total Orders', value: numf(d.total_orders) },
          { label: 'Total Units', value: numf(d.total_units) },
        ],
        columns: [
          { header: 'Metric', key: 'metric' },
          { header: 'Value', key: 'value', align: 'right' },
        ],
        rows: [
          { metric: 'Total Orders', value: numf(d.total_orders) },
          { metric: 'Total Units Ordered', value: numf(d.total_units) },
        ],
      }
    }
    if (active === 'cust_orders') {
      const list = arr(custOrdersQ.data)
      return {
        loading: custOrdersQ.isLoading,
        columns: [
          { header: 'Code', key: 'code' },
          { header: 'Customer', key: 'name' },
          { header: 'Tier', key: 'tier_label' },
          { header: 'Orders', key: 'orders', align: 'right', print: r => numf(r.orders) },
          { header: 'Units Ordered', key: 'units_label', align: 'right', csv: r => r.units, print: r => numf(r.units) },
        ],
        rows: list.map(c => ({ ...c, tier_label: 'Tier ' + (c.tier || 2), units_label: numf(c.units) })),
      }
    }
    if (active === 'promo_perf') {
      const list = arr(promoPerfQ.data)
      return {
        loading: promoPerfQ.isLoading,
        columns: [
          { header: 'Promotion', key: 'title' },
          { header: 'Code', key: 'promo_code' },
          { header: 'Reach', key: 'reach', align: 'right', print: r => numf(r.reach) },
          { header: 'Views', key: 'views', align: 'right', print: r => numf(r.views) },
          { header: 'View Rate', key: 'view_rate_label', align: 'right', csv: r => r.view_rate, print: r => r.view_rate + '%' },
          { header: 'Redemptions', key: 'redemptions', align: 'right', print: r => numf(r.redemptions) },
          { header: 'Conversion', key: 'conversion_label', align: 'right', csv: r => r.conversion, print: r => r.conversion + '%' },
          { header: 'Units', key: 'units_label', align: 'right', csv: r => r.units, print: r => numf(r.units) },
        ],
        rows: list.map(p => ({ ...p, view_rate_label: p.view_rate + '%', conversion_label: p.conversion + '%', units_label: numf(p.units) })),
      }
    }
    if (active === 'top') {
      const list = arr(topQ.data)
      return {
        loading: topQ.isLoading,
        chart: {
          options: {
            chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent' },
            plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '65%' } },
            colors: ['#0e947a'], dataLabels: { enabled: false },
            xaxis: { categories: list.slice(0, 10).map(p => (p.name || p.stock_code || '').slice(0, 22)), labels: { style: { colors: '#9ca3af' } } },
            yaxis: { labels: { style: { colors: '#9ca3af' } } }, grid: { borderColor: '#282828' }, theme: { mode: 'dark' },
          },
          series: [{ name: 'Units Sold', data: list.slice(0, 10).map(p => Math.round(p.units || 0)) }],
        },
        columns: [
          { header: '#', key: 'rank', align: 'right' },
          { header: 'Product', key: 'name' },
          { header: 'Brand', key: 'brand' },
          { header: 'Stock Code', key: 'stock_code' },
          { header: 'Units Sold', key: 'units_label', align: 'right', csv: r => r.units, print: r => numf(r.units) },
        ],
        rows: list.map((p, i) => ({ ...p, rank: i + 1, units_label: numf(p.units) })),
      }
    }
    if (active === 'categories') {
      const list = arr(catQ.data)
      return {
        loading: catQ.isLoading,
        chart: {
          options: {
            chart: { type: 'donut', fontFamily: 'inherit', background: 'transparent' },
            labels: list.map(c => c.name), legend: { position: 'bottom', labels: { colors: '#9ca3af' } },
            dataLabels: { enabled: false }, theme: { mode: 'dark' }, stroke: { width: 0 },
          },
          series: list.map(c => c.product_count || 0),
        },
        columns: [
          { header: 'Category', key: 'name' },
          { header: 'Products', key: 'product_count', align: 'right', print: r => numf(r.product_count) },
        ],
        rows: list,
      }
    }
    if (active === 'catalogue') {
      const list = arr(catalogueQ.data)
      return {
        loading: catalogueQ.isLoading,
        columns: [
          { header: 'Stock Code', key: 'stock_code' },
          { header: 'Product', key: 'description' },
          { header: 'Brand', key: 'brand' },
          { header: 'Category', key: 'cat', csv: r => catName[r.category_id] || '', print: r => catName[r.category_id] || '-' },
          { header: 'Pack', key: 'packsize', align: 'right' },
          { header: 'Price (Incl)', key: 'price', align: 'right', csv: r => Number(r.inclprice2 || 0).toFixed(2), print: r => money(r.inclprice2) },
          { header: 'Active', key: 'act' },
        ],
        rows: list.map(p => ({ ...p, cat: catName[p.category_id] || '-', price: money(p.inclprice2), act: p.active ? 'Yes' : 'No' })),
      }
    }
    if (active === 'customers') {
      const list = arr(custQ.data)
      return {
        loading: custQ.isLoading,
        columns: [
          { header: 'Code', key: 'code' },
          { header: 'Name', key: 'name' },
          { header: 'Email', key: 'email' },
          { header: 'Phone', key: 'phone' },
          { header: 'Tier', key: 'tier', align: 'right' },
          { header: 'Active', key: 'act' },
        ],
        rows: list.map(c => ({ ...c, tier: 'Tier ' + (c.price_list_tier || 2), act: c.active ? 'Yes' : 'No' })),
      }
    }
    return { columns: [], rows: [], loading: false }
  }, [active, salesQ.data, salesQ.isLoading, custOrdersQ.data, custOrdersQ.isLoading, promoPerfQ.data, promoPerfQ.isLoading, topQ.data, topQ.isLoading, catQ.data, catQ.isLoading, catalogueQ.data, catalogueQ.isLoading, custQ.data, custQ.isLoading, catName])

  const subtitle = active === 'sales' ? `${from} to ${to}` : ''

  const onExport = () => {
    if (!rows.length) { toast.error('Nothing to export'); return }
    downloadCSV(`${active}-report-${today}.csv`, toCSV(columns, rows))
    toast.success('CSV downloaded')
  }
  const onPrint = () => {
    if (!rows.length) { toast.error('Nothing to print'); return }
    printReport(report.label, subtitle, columns, rows)
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <FileBarChart size={20} className="text-primary" /> Reports
        </h1>
        <div className="flex gap-2">
          <button onClick={onPrint} className="btn-secondary flex items-center gap-1.5 text-sm">
            <Printer size={15} /> Print
          </button>
          <button onClick={onExport} className="btn-primary flex items-center gap-1.5 text-sm">
            <Download size={15} /> Export CSV
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-400 mb-4">Generate, view, print and export operational reports. (Charts live on the Dashboard.)</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {REPORTS.map(r => {
          const Icon = r.icon
          return (
            <button key={r.key} onClick={() => setActive(r.key)}
              className={`px-3.5 py-2 rounded-xl border text-sm font-medium transition-colors flex items-center gap-1.5 ${active === r.key ? 'bg-primary/10 border-primary/50 text-primary' : 'bg-black/40 border-[#323232] text-gray-400 hover:text-gray-200'}`}>
              <Icon size={14} /> {r.label}
            </button>
          )
        })}
      </div>

      {report?.dated && (
        <div className="flex items-end gap-3 mb-4 bg-[#161616] border border-[#282828] rounded-xl p-3">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="block bg-black/40 border border-[#323232] rounded-lg px-3 py-1.5 text-white text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="block bg-black/40 border border-[#323232] rounded-lg px-3 py-1.5 text-white text-sm" />
          </div>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {summary.map(s => (
            <div key={s.label} className="bg-[#161616] border border-[#282828] rounded-xl p-4">
              <div className="text-2xl font-bold text-primary">{s.value}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {chart && rows.length > 0 && (
        <div className="bg-[#161616] border border-[#282828] rounded-2xl p-4 mb-4">
          <ReactApexChart options={chart.options} series={chart.series} type={chart.options.chart.type} height={280} />
        </div>
      )}

      <div className="bg-[#161616] border border-[#282828] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : rows.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-12">No data for this report.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  {columns.map(c => (
                    <th key={c.header} className={`px-4 py-2.5 font-semibold ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-t border-[#222] hover:bg-white/5">
                    {columns.map(c => (
                      <td key={c.header} className={`px-4 py-2 text-gray-300 whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                        {row[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {rows.length > 0 && <p className="text-xs text-gray-600 mt-2">{numf(rows.length)} rows</p>}
    </div>
  )
}
